import React, { useState, useRef, useMemo } from "react";
import { useSession } from "hooks/useSession";
import { fetchWrapper } from "utils/apiFetch";

/* ─────────────────────────────────────────────────────────────────────────
   PAN Card Application — React port of the 7-step navy/gold wizard
   (Form 49A / Form CR — Shree Ram Enterprises)
───────────────────────────────────────────────────────────────────────── */

const STEP_LABELS = ["Personal", "Parents", "Address", "Contact", "Income & Docs", "Photos", "Review"];
const TOTAL_STEPS = STEP_LABELS.length;

const FILE_FIELDS = [
  { id: "photo_file", label: "Photo", icon: "👤", hint: "Passport size (4.5×3.5 cm)" },
  { id: "signature_file", label: "Signature", icon: "✒️", hint: "Clear signature on white background" },
  { id: "aadharfront_file", label: "Aadhaar Front", icon: "📄", hint: "Aadhaar front side" },
  { id: "aadharback_file", label: "Aadhaar Back", icon: "📄", hint: "Aadhaar back side" },
  { id: "doc1_file", label: "Supporting Doc 1", icon: "📋", hint: "POI / POA / PODoB document" },
  { id: "doc2_file", label: "Supporting Doc 2", icon: "📋", hint: "Additional supporting document" },
];

const INCOME_OPTIONS = [
  { value: "salary", label: "Salary" },
  { value: "business", label: "Business / Profession" },
  { value: "house_property", label: "House Property" },
  { value: "capital_gains", label: "Capital Gains" },
  { value: "other_sources", label: "Other Sources" },
  { value: "no_income", label: "No Income" },
];

const POI_OPTIONS = ["AADHAAR Card issued by UIDAI", "Passport", "Voter ID Card", "Driving License", "Ration Card", "Bank Certificate"];
const POA_OPTIONS = ["AADHAAR Card issued by UIDAI", "Passport", "Voter ID Card", "Driving License", "Electricity Bill", "Bank Statement"];
const POD_OPTIONS = ["Central Govt Health Scheme Card", "AADHAAR Card issued by UIDAI", "Passport", "Birth Certificate", "Matriculation Certificate", "Driving License"];

const initialForm = {
  first_name: "", middle_name: "", last_name: "",
  name_as_per_aadhaar: "",
  gender: "", dob: "",
  aadharnum: "", application_number: "",
  is_minor: false,
  father_first: "", father_middle: "", father_last: "",
  mother_first: "", mother_middle: "", mother_last: "",
  parent_on_card: "Father",
  res_flat: "", res_road: "", res_po: "", res_area: "",
  res_district: "", res_state: "", res_pin: "", res_country: "INDIA",
  comm_address: "Residence",
  ao_area_code: "", ao_type: "", ao_range_code: "", ao_no: "",
  place: "", date: "",
  mobile: "", email: "",
  income: [],
  poi_doc: POI_OPTIONS[0],
  poa_doc: POA_OPTIONS[0],
  pod_doc: POD_OPTIONS[0],
};

/* ── Small presentational helpers ───────────────────────────────────────── */

const SubLabel = ({ num, children }) => (
  <div className="pan2-sub-label">
    <span>{num}</span>{children}
  </div>
);

const Field = ({ label, required, span, children, hint }) => (
  <div className={`pan2-field${span ? ` pan2-col-span-${span}` : ""}`}>
    <label>{label} {required && <span className="pan2-req">*</span>}</label>
    {children}
    {hint && <span className="pan2-hint">{hint}</span>}
  </div>
);

const RadioPill = ({ name, value, label, checked, onChange }) => (
  <label className="pan2-radio-pill">
    <input type="radio" name={name} value={value} checked={checked} onChange={onChange} />
    {label}
  </label>
);

const CheckPill = ({ label, checked, onChange }) => (
  <label className="pan2-check-pill">
    <input type="checkbox" checked={checked} onChange={onChange} />
    {label}
  </label>
);

const UploadBox = ({ field, file, preview, onPick }) => {
  const inputRef = useRef(null);
  return (
    <div className="pan2-sub-section">
      <SubLabel num={field.icon}>{field.label}</SubLabel>
      <div className="pan2-upload-box" onClick={() => inputRef.current?.click()}>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          style={{ display: "none" }}
          onChange={(e) => onPick(field.id, e.target.files?.[0] || null)}
        />
        {preview ? (
          <img src={preview} className="pan2-upload-preview pan2-show" alt={field.label} />
        ) : (
          <div className="pan2-upload-icon">{field.icon === "👤" ? "👤" : field.icon}</div>
        )}
        <div className="pan2-up-label">
          <strong>{file ? file.name : "Click to upload"}</strong>
          <br />
          {field.hint}
        </div>
      </div>
    </div>
  );
};

/* ── Main component ─────────────────────────────────────────────────────── */

export default function PanCardApplication() {
  const { userid } = useSession();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState({});
  const [previews, setPreviews] = useState({});
  const [submitResult, setSubmitResult] = useState(null);
  const [transactionPin, setTransactionPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const set = (key, val) => setForm((p) => ({ ...p, [key]: val }));
  const upper = (key) => (e) => set(key, e.target.value.toUpperCase());
  const plain = (key) => (e) => set(key, e.target.value);
  const digits = (key, max) => (e) => {
    let v = e.target.value.replace(/\D/g, "");
    if (max) v = v.slice(0, max);
    set(key, v);
  };

  const handleAadhaar = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 12);
    const grouped = digitsOnly.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
    set("aadharnum", grouped);
  };

  const toggleIncome = (value) => {
    setForm((p) => {
      const has = p.income.includes(value);
      return { ...p, income: has ? p.income.filter((v) => v !== value) : [...p.income, value] };
    });
  };

  const pickFile = (fieldId, file) => {
    setFiles((p) => ({ ...p, [fieldId]: file }));
    if (file && file.type?.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setPreviews((p) => ({ ...p, [fieldId]: e.target.result }));
      reader.readAsDataURL(file);
    } else {
      setPreviews((p) => ({ ...p, [fieldId]: null }));
    }
  };

  const goStep = (n) => {
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      if (n > currentStep) next.add(currentStep);
      else next.delete(n);
      return next;
    });
    setCurrentStep(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fullName = (a, b, c) => [a, b, c].filter(Boolean).join(" ");

  const summaryItems = useMemo(
    () => [
      ["First Name", form.first_name],
      ["Middle Name", form.middle_name],
      ["Last Name", form.last_name],
      ["Aadhaar Name", form.name_as_per_aadhaar],
      ["Gender", form.gender],
      ["Date of Birth", form.dob],
      ["Aadhaar Number", form.aadharnum],
      ["Mobile", form.mobile],
      ["Email", form.email],
      ["Father Name", fullName(form.father_first, form.father_middle, form.father_last)],
      ["Mother Name", fullName(form.mother_first, form.mother_middle, form.mother_last)],
      ["Parent on Card", form.parent_on_card],
      ["District", form.res_district],
      ["State", form.res_state],
      ["PIN Code", form.res_pin],
      ["Income Sources", form.income.join(", ") || "-"],
      ["POI Document", form.poi_doc],
      ["POA Document", form.poa_doc],
      ["PODoB Document", form.pod_doc],
      ["Place", form.place],
      ["Declaration Date", form.date],
      ["Minor Applicant", form.is_minor ? "Yes" : "No"],
    ],
    [form]
  );

  const buildPayload = () => ({
    first_name: form.first_name,
    middle_name: form.middle_name,
    last_name: form.last_name,
    fullname: fullName(form.first_name, form.middle_name, form.last_name),
    name_as_per_aadhaar: form.name_as_per_aadhaar,
    gender: form.gender,
    dob: form.dob,
    aadharnum: form.aadharnum.replace(/\s/g, ""),
    mobile: "91" + form.mobile,
    email: form.email,
    father_first: form.father_first,
    father_middle: form.father_middle,
    father_last: form.father_last,
    fathername: fullName(form.father_first, form.father_middle, form.father_last),
    mother_first: form.mother_first,
    mother_middle: form.mother_middle,
    mother_last: form.mother_last,
    parent_on_card: form.parent_on_card,
    income_sources: form.income,
    res_flat: form.res_flat,
    res_road: form.res_road,
    res_po: form.res_po,
    res_area: form.res_area,
    res_district: form.res_district,
    res_state: form.res_state,
    res_pin: form.res_pin,
    res_country: form.res_country || "INDIA",
    ao_area_code: form.ao_area_code,
    ao_type: form.ao_type,
    ao_range_code: form.ao_range_code,
    ao_no: form.ao_no,
    comm_address: form.comm_address || "Residence",
    poi_doc: form.poi_doc,
    poa_doc: form.poa_doc,
    pod_doc: form.pod_doc,
    place: form.place,
    date: form.date,
    application_number: form.application_number,
    is_minor: form.is_minor ? "true" : "false",
  });

  // Maps this component's upload field ids -> the request.FILES[...] keys
  // expected by MannualNewPanManager's "New Application" action.
  const FILE_KEY_MAP = {
    photo_file: "photo",
    signature_file: "signature",
    aadharfront_file: "aadharfront",
    aadharback_file: "aadharback",
    doc1_file: "doc1",
    doc2_file: "doc2",
  };

  const REQUIRED_FILE_FIELDS = [
    { id: "photo_file", label: "Photo" },
    { id: "signature_file", label: "Signature" },
    { id: "aadharfront_file", label: "Aadhaar Front" },
    { id: "aadharback_file", label: "Aadhaar Back" },
  ];

  const submitForm = async () => {
    setSubmitError("");

    // ── Client-side checks before hitting the API ──────────────────────
    const missingFiles = REQUIRED_FILE_FIELDS.filter((f) => !files[f.id]);
    if (missingFiles.length > 0) {
      setSubmitError(
        `Please upload: ${missingFiles.map((f) => f.label).join(", ")} (Step 6: Photos)`
      );
      return;
    }
    if (!/^\d{4}$/.test(transactionPin)) {
      setSubmitError("Enter your 4-digit transaction PIN to confirm submission.");
      return;
    }

    const data = buildPayload();
    setSubmitResult(null);
    setSubmitting(true);

    try {
      const fd = new FormData();
      fd.append("action", "New Application");
      fd.append("userid", userid);
      fd.append("pin", transactionPin);
      fd.append("aadhar_addess", "aadhar"); // this wizard always uses the Aadhaar address flow

      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => fd.append(key, v));
        } else if (value !== undefined && value !== null) {
          fd.append(key, value);
        }
      });

      Object.entries(FILE_KEY_MAP).forEach(([localId, serverKey]) => {
        if (files[localId]) fd.append(serverKey, files[localId]);
      });

      const res = await fetchWrapper("https://api.mokshiq.in/pan_action_manager", {
        method: "POST",
        body: fd,
      });

      if (!res?.status) {
        setSubmitError(res?.data || "Submission failed. Please try again.");
        return;
      }

      setSubmitResult({
        reference_num: res.reference_num,
        pdf_form93: res.pdf_form93,
        pdf_formcr: res.pdf_formcr,
        pdf_bundle: res.pdf_bundle,
        message: res.data,
      });
    } catch (err) {
      setSubmitError("Network error — please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = (currentStep / TOTAL_STEPS) * 100;

  return (
    <div className="pan2-root">
      <style>{CSS}</style>

      <div className="pan2-header">
        <div>
          <h1>🪪 PAN Card Application</h1>
          <p>Form 49A / Form CR — Shree Ram Enterprises</p>
        </div>
        <div className="pan2-header-badge">Agent: 87520</div>
      </div>

      <div className="pan2-form-wrap">
        {/* Step nav */}
        <div className="pan2-steps">
          {STEP_LABELS.map((label, i) => {
            const n = i + 1;
            const cls = n === currentStep ? "active" : completedSteps.has(n) ? "done" : "";
            return (
              <button key={n} className={`pan2-step-btn ${cls}`} onClick={() => goStep(n)}>
                <span className="pan2-step-num">{n}</span>
                {label}
              </button>
            );
          })}
        </div>
        <div className="pan2-progress-bar">
          <div className="pan2-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>

        {/* STEP 1: Personal */}
        {currentStep === 1 && (
          <div className="pan2-section pan2-active">
            <div className="pan2-section-head"><h2>Personal Information</h2><span className="pan2-badge">Part A</span></div>
            <div className="pan2-section-body">

              <div className="pan2-sub-section">
                <SubLabel num="1">Full Name</SubLabel>
                <div className="pan2-grid-3">
                  <Field label="First Name" required>
                    <input type="text" value={form.first_name} onChange={upper("first_name")} placeholder="e.g. KUMKUM" />
                  </Field>
                  <Field label="Middle Name">
                    <input type="text" value={form.middle_name} onChange={upper("middle_name")} placeholder="(if any)" />
                  </Field>
                  <Field label="Last Name / Surname" required>
                    <input type="text" value={form.last_name} onChange={upper("last_name")} placeholder="e.g. MAURYA" />
                  </Field>
                </div>
              </div>

              <div className="pan2-sub-section">
                <SubLabel num="1B">Name as per Aadhaar</SubLabel>
                <Field label="Aadhaar Name" required hint="This name will be printed on your PAN card">
                  <input type="text" value={form.name_as_per_aadhaar} onChange={upper("name_as_per_aadhaar")} placeholder="Exactly as on Aadhaar card" />
                </Field>
              </div>

              <div className="pan2-sub-section">
                <SubLabel num="2">Gender & Date of Birth</SubLabel>
                <div className="pan2-grid-2">
                  <Field label="Gender" required>
                    <div className="pan2-radio-group">
                      {["Male", "Female", "Transgender"].map((g) => (
                        <RadioPill key={g} name="gender" value={g} label={g} checked={form.gender === g} onChange={plain("gender")} />
                      ))}
                    </div>
                  </Field>
                  <Field label="Date of Birth" required>
                    <input type="date" value={form.dob} onChange={plain("dob")} />
                  </Field>
                </div>
              </div>

              <div className="pan2-sub-section">
                <SubLabel num="4">Aadhaar Number</SubLabel>
                <div className="pan2-grid-2">
                  <Field label="Aadhaar Number" required>
                    <input type="text" maxLength={14} value={form.aadharnum} onChange={handleAadhaar} placeholder="XXXX XXXX XXXX" />
                  </Field>
                  <Field label="Application Number">
                    <input type="text" value={form.application_number} onChange={plain("application_number")} placeholder="(auto-generated if blank)" />
                  </Field>
                </div>
              </div>

              <div className="pan2-sub-section">
                <SubLabel num="12">Minor Applicant</SubLabel>
                <Field label="" hint="Selecting Minor will auto-fill Representative Assessee (Part E) with Father's details">
                  <div className="pan2-radio-group">
                    <RadioPill name="is_minor" value="false" label="Adult (18+)" checked={!form.is_minor} onChange={() => set("is_minor", false)} />
                    <RadioPill name="is_minor" value="true" label="Minor (below 18)" checked={form.is_minor} onChange={() => set("is_minor", true)} />
                  </div>
                </Field>
              </div>

              <div className="pan2-nav-btns">
                <div />
                <button className="pan2-btn pan2-btn-next" onClick={() => goStep(2)}>Next: Parents →</button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Parents */}
        {currentStep === 2 && (
          <div className="pan2-section pan2-active">
            <div className="pan2-section-head"><h2>Parent Details</h2><span className="pan2-badge">Part C</span></div>
            <div className="pan2-section-body">

              <div className="pan2-sub-section">
                <SubLabel num="13">Father's Name</SubLabel>
                <div className="pan2-grid-3">
                  <Field label="First Name" required><input type="text" value={form.father_first} onChange={upper("father_first")} /></Field>
                  <Field label="Middle Name"><input type="text" value={form.father_middle} onChange={upper("father_middle")} /></Field>
                  <Field label="Last Name" required><input type="text" value={form.father_last} onChange={upper("father_last")} /></Field>
                </div>
              </div>

              <div className="pan2-sub-section">
                <SubLabel num="14">Mother's Name</SubLabel>
                <div className="pan2-grid-3">
                  <Field label="First Name"><input type="text" value={form.mother_first} onChange={upper("mother_first")} /></Field>
                  <Field label="Middle Name"><input type="text" value={form.mother_middle} onChange={upper("mother_middle")} /></Field>
                  <Field label="Last Name"><input type="text" value={form.mother_last} onChange={upper("mother_last")} /></Field>
                </div>
              </div>

              <div className="pan2-sub-section">
                <SubLabel num="15">Name to Print on PAN Card</SubLabel>
                <Field label="">
                  <div className="pan2-radio-group">
                    <RadioPill name="parent_on_card" value="Father" label="Father's Name" checked={form.parent_on_card === "Father"} onChange={plain("parent_on_card")} />
                    <RadioPill name="parent_on_card" value="Mother" label="Mother's Name" checked={form.parent_on_card === "Mother"} onChange={plain("parent_on_card")} />
                  </div>
                </Field>
              </div>

              <div className="pan2-nav-btns">
                <button className="pan2-btn pan2-btn-prev" onClick={() => goStep(1)}>← Back</button>
                <button className="pan2-btn pan2-btn-next" onClick={() => goStep(3)}>Next: Address →</button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Address */}
        {currentStep === 3 && (
          <div className="pan2-section pan2-active">
            <div className="pan2-section-head"><h2>Residence Address</h2><span className="pan2-badge">Field 5</span></div>
            <div className="pan2-section-body">

              <div className="pan2-sub-section">
                <SubLabel num="5">Residence Address</SubLabel>
                <div className="pan2-grid-2">
                  <Field label="Flat / Door / Building" required span={2}><input type="text" value={form.res_flat} onChange={upper("res_flat")} /></Field>
                  <Field label="Road / Street / Block / Sector" span={2}><input type="text" value={form.res_road} onChange={upper("res_road")} /></Field>
                  <Field label="Post Office"><input type="text" value={form.res_po} onChange={upper("res_po")} /></Field>
                  <Field label="Area / Locality / Town / City"><input type="text" value={form.res_area} onChange={upper("res_area")} /></Field>
                  <Field label="District" required><input type="text" value={form.res_district} onChange={upper("res_district")} /></Field>
                  <Field label="State / Union Territory" required><input type="text" value={form.res_state} onChange={upper("res_state")} /></Field>
                  <Field label="PIN Code" required><input type="text" maxLength={6} value={form.res_pin} onChange={digits("res_pin", 6)} placeholder="6 digits" /></Field>
                  <Field label="Country"><input type="text" value={form.res_country} onChange={upper("res_country")} /></Field>
                </div>
              </div>

              <div className="pan2-sub-section">
                <SubLabel num="7">Communication Address</SubLabel>
                <Field label="">
                  <div className="pan2-radio-group">
                    <RadioPill name="comm_address" value="Residence" label="Residence" checked={form.comm_address === "Residence"} onChange={plain("comm_address")} />
                    <RadioPill name="comm_address" value="Office" label="Office" checked={form.comm_address === "Office"} onChange={plain("comm_address")} />
                  </div>
                </Field>
              </div>

              <div className="pan2-sub-section">
                <SubLabel num="16">AO Code (Assessing Officer)</SubLabel>
                <div className="pan2-ao-grid">
                  <Field label="Area Code"><input type="text" value={form.ao_area_code} onChange={upper("ao_area_code")} placeholder="e.g. DEL" /></Field>
                  <Field label="AO Type"><input type="text" value={form.ao_type} onChange={plain("ao_type")} placeholder="e.g. W" /></Field>
                  <Field label="Range Code"><input type="text" value={form.ao_range_code} onChange={plain("ao_range_code")} placeholder="e.g. 123" /></Field>
                  <Field label="AO Number"><input type="text" value={form.ao_no} onChange={plain("ao_no")} placeholder="e.g. 12" /></Field>
                </div>
              </div>

              <div className="pan2-sub-section">
                <SubLabel num="8">Declaration Place & Date</SubLabel>
                <div className="pan2-grid-2">
                  <Field label="Place" required><input type="text" value={form.place} onChange={upper("place")} /></Field>
                  <Field label="Date" required><input type="date" value={form.date} onChange={plain("date")} /></Field>
                </div>
              </div>

              <div className="pan2-nav-btns">
                <button className="pan2-btn pan2-btn-prev" onClick={() => goStep(2)}>← Back</button>
                <button className="pan2-btn pan2-btn-next" onClick={() => goStep(4)}>Next: Contact →</button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 4: Contact */}
        {currentStep === 4 && (
          <div className="pan2-section pan2-active">
            <div className="pan2-section-head"><h2>Contact Details</h2><span className="pan2-badge">Field 10</span></div>
            <div className="pan2-section-body">

              <div className="pan2-sub-section">
                <SubLabel num="10">Mobile & Email</SubLabel>
                <div className="pan2-grid-2">
                  <Field label="Mobile Number" required hint="Enter 10-digit number without country code">
                    <input type="tel" maxLength={10} value={form.mobile} onChange={digits("mobile", 10)} placeholder="10-digit mobile" />
                  </Field>
                  <Field label="Email ID" required>
                    <input type="email" value={form.email} onChange={plain("email")} placeholder="example@email.com" />
                  </Field>
                </div>
              </div>

              <div className="pan2-nav-btns">
                <button className="pan2-btn pan2-btn-prev" onClick={() => goStep(3)}>← Back</button>
                <button className="pan2-btn pan2-btn-next" onClick={() => goStep(5)}>Next: Income & Docs →</button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 5: Income & Docs */}
        {currentStep === 5 && (
          <div className="pan2-section pan2-active">
            <div className="pan2-section-head"><h2>Income & Documents</h2><span className="pan2-badge">Part B & G</span></div>
            <div className="pan2-section-body">

              <div className="pan2-sub-section">
                <SubLabel num="11">Source of Income</SubLabel>
                <div className="pan2-check-grid">
                  {INCOME_OPTIONS.map((opt) => (
                    <CheckPill
                      key={opt.value}
                      label={opt.label}
                      checked={form.income.includes(opt.value)}
                      onChange={() => toggleIncome(opt.value)}
                    />
                  ))}
                </div>
              </div>

              <div className="pan2-sub-section">
                <SubLabel num="23">Proof Documents</SubLabel>
                <div className="pan2-grid-3">
                  <Field label="Proof of Identity" required>
                    <select value={form.poi_doc} onChange={plain("poi_doc")}>
                      {POI_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Proof of Address" required>
                    <select value={form.poa_doc} onChange={plain("poa_doc")}>
                      {POA_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Proof of Date of Birth" required>
                    <select value={form.pod_doc} onChange={plain("pod_doc")}>
                      {POD_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>
              </div>

              <div className="pan2-nav-btns">
                <button className="pan2-btn pan2-btn-prev" onClick={() => goStep(4)}>← Back</button>
                <button className="pan2-btn pan2-btn-next" onClick={() => goStep(6)}>Next: Photos →</button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 6: Photos */}
        {currentStep === 6 && (
          <div className="pan2-section pan2-active">
            <div className="pan2-section-head"><h2>Photos & Signature</h2><span className="pan2-badge">Upload</span></div>
            <div className="pan2-section-body">
              <div className="pan2-grid-3">
                {FILE_FIELDS.map((f) => (
                  <UploadBox key={f.id} field={f} file={files[f.id]} preview={previews[f.id]} onPick={pickFile} />
                ))}
              </div>

              <div className="pan2-nav-btns">
                <button className="pan2-btn pan2-btn-prev" onClick={() => goStep(5)}>← Back</button>
                <button className="pan2-btn pan2-btn-next" onClick={() => goStep(7)}>Review & Submit →</button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 7: Review */}
        {currentStep === 7 && (
          <div className="pan2-section pan2-active">
            <div className="pan2-section-head"><h2>Review & Submit</h2><span className="pan2-badge">Final</span></div>
            <div className="pan2-section-body">
              <div className="pan2-summary-grid">
                {summaryItems.map(([label, value]) => (
                  <div className="pan2-summary-item" key={label}>
                    <div className="pan2-s-label">{label}</div>
                    <div className="pan2-s-value">{value || "—"}</div>
                  </div>
                ))}
              </div>

              {!submitResult && (
                <div className="pan2-sub-section pan2-pin-section">
                  <SubLabel num="🔒">Confirm Submission</SubLabel>
                  <Field label="Transaction PIN" required hint="Your 4-digit wallet PIN, used to confirm this request.">
                    <input
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      value={transactionPin}
                      onChange={(e) => setTransactionPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      placeholder="••••"
                    />
                  </Field>
                </div>
              )}

              {submitError && (
                <div className="pan2-submit-error">{submitError}</div>
              )}

              {submitResult && (
                <div className="pan2-submit-result pan2-submit-success">
                  <strong>{submitResult.message || "Request submitted successfully."}</strong>
                  {submitResult.reference_num && (
                    <div className="pan2-ref-no">Reference No: {submitResult.reference_num}</div>
                  )}
                  <div className="pan2-pdf-links">
                    {submitResult.pdf_form93 && (
                      <a href={`https://api.mokshiq.in${submitResult.pdf_form93}`} target="_blank" rel="noreferrer">Form 93 (PDF)</a>
                    )}
                    {submitResult.pdf_formcr && (
                      <a href={`https://api.mokshiq.in${submitResult.pdf_formcr}`} target="_blank" rel="noreferrer">Form CR (PDF)</a>
                    )}
                    {submitResult.pdf_bundle && (
                     <a href={`https://api.mokshiq.in${submitResult.pdf_bundle}`} target="_blank" rel="noreferrer">Full Bundle (PDF)</a>
                    )}
                  </div>
                </div>
              )}

              <div className="pan2-nav-btns">
                <button className="pan2-btn pan2-btn-prev" onClick={() => goStep(6)} disabled={submitting}>← Edit</button>
                {!submitResult && (
                  <button className="pan2-btn pan2-btn-submit" onClick={submitForm} disabled={submitting}>
                    {submitting ? "Submitting…" : "⬇ Generate PDF Forms"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── CSS (ported 1:1 from the HTML version, classes prefixed pan2- to avoid collisions) ── */
const CSS = `
.pan2-root {
  --navy:    #1a237e;
  --gold:    #c8a84b;
  --light:   #f5f7ff;
  --border:  #dde1f0;
  --text:    #1c1e2e;
  --muted:   #6b7087;
  --error:   #c0392b;
  --success: #1a7e4a;
  --input-h: 38px;
  --radius:  6px;
  font-family: 'Segoe UI', system-ui, sans-serif;
  background: #eef0f8;
  color: var(--text);
  font-size: 14px;
}
.pan2-root * { box-sizing: border-box; }

.pan2-header {
  background: var(--navy); color: #fff; padding: 18px 32px;
  display: flex; align-items: center; gap: 16px;
  border-bottom: 3px solid var(--gold);
}
.pan2-header h1 { font-size: 20px; font-weight: 700; letter-spacing: .3px; margin: 0; }
.pan2-header p { font-size: 12px; opacity: .75; margin-top: 2px; }
.pan2-header-badge {
  margin-left: auto; background: var(--gold); color: var(--navy);
  font-weight: 700; font-size: 11px; padding: 4px 10px; border-radius: 20px;
}

.pan2-form-wrap { max-width: 960px; margin: 28px auto; padding: 0 16px 60px; }

.pan2-steps { display: flex; gap: 0; margin-bottom: 24px; border-radius: var(--radius); overflow: hidden; border: 1px solid var(--border); }
.pan2-step-btn {
  flex: 1; padding: 10px 8px; text-align: center; background: #fff;
  border: none; cursor: pointer; font-size: 12px; font-weight: 600;
  color: var(--muted); border-right: 1px solid var(--border);
  transition: all .2s;
}
.pan2-step-btn:last-child { border-right: none; }
.pan2-step-btn.active { background: var(--navy); color: #fff; }
.pan2-step-btn.done { background: #e8f5ee; color: var(--success); }
.pan2-step-num { display: block; font-size: 18px; font-weight: 700; line-height: 1; margin-bottom: 3px; }

.pan2-section {
  background: #fff; border-radius: var(--radius);
  border: 1px solid var(--border); margin-bottom: 16px;
}
.pan2-section-head {
  background: var(--navy); color: #fff; padding: 12px 20px;
  border-radius: var(--radius) var(--radius) 0 0;
  display: flex; align-items: center; gap: 10px;
}
.pan2-section-head h2 { font-size: 14px; font-weight: 700; margin: 0; }
.pan2-section-head .pan2-badge {
  background: var(--gold); color: var(--navy);
  font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 12px;
}
.pan2-section-body { padding: 20px; }

.pan2-sub-section { margin-bottom: 24px; }
.pan2-sub-label {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .8px; color: var(--navy); margin-bottom: 12px;
  padding-bottom: 6px; border-bottom: 2px solid var(--gold);
  display: flex; align-items: center; gap: 8px;
}
.pan2-sub-label span { background: var(--gold); color: var(--navy); width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; flex-shrink: 0; }

.pan2-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.pan2-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
.pan2-col-span-2 { grid-column: span 2; }
.pan2-col-span-3 { grid-column: span 3; }
@media(max-width:640px) {
  .pan2-grid-2, .pan2-grid-3 { grid-template-columns: 1fr; }
  .pan2-col-span-2, .pan2-col-span-3 { grid-column: span 1; }
}

.pan2-field { display: flex; flex-direction: column; gap: 4px; }
.pan2-field label { font-size: 12px; font-weight: 600; color: var(--muted); }
.pan2-field label .pan2-req { color: var(--error); margin-left: 2px; }
.pan2-field input, .pan2-field select, .pan2-field textarea {
  height: var(--input-h); padding: 0 10px; border: 1.5px solid var(--border);
  border-radius: var(--radius); font-size: 13px; color: var(--text);
  background: #fff; transition: border-color .15s; font-family: inherit;
  width: 100%;
}
.pan2-field input:focus, .pan2-field select:focus, .pan2-field textarea:focus {
  outline: none; border-color: var(--navy); background: var(--light);
}
.pan2-field .pan2-hint { font-size: 11px; color: var(--muted); }

.pan2-radio-group { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 2px; }
.pan2-radio-pill {
  display: flex; align-items: center; gap: 6px; padding: 6px 14px;
  border: 1.5px solid var(--border); border-radius: 20px; cursor: pointer;
  font-size: 13px; transition: all .15s; user-select: none;
}
.pan2-radio-pill input { accent-color: var(--navy); width: 14px; height: 14px; }
.pan2-radio-pill:has(input:checked) { border-color: var(--navy); background: var(--light); color: var(--navy); font-weight: 600; }

.pan2-check-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px,1fr)); gap: 8px; margin-top: 4px; }
.pan2-check-pill {
  display: flex; align-items: center; gap: 8px; padding: 8px 12px;
  border: 1.5px solid var(--border); border-radius: var(--radius); cursor: pointer;
  font-size: 12px; transition: all .15s;
}
.pan2-check-pill input { accent-color: var(--navy); width: 14px; height: 14px; }
.pan2-check-pill:has(input:checked) { border-color: var(--navy); background: var(--light); color: var(--navy); font-weight: 600; }

.pan2-upload-box {
  border: 2px dashed var(--border); border-radius: var(--radius);
  padding: 16px; text-align: center; cursor: pointer;
  transition: all .2s; position: relative; background: #fafbff;
}
.pan2-upload-box:hover { border-color: var(--navy); background: var(--light); }
.pan2-upload-icon { font-size: 24px; margin-bottom: 4px; }
.pan2-up-label { font-size: 12px; color: var(--muted); }
.pan2-up-label strong { color: var(--navy); }
.pan2-upload-preview { width: 80px; height: 80px; object-fit: cover; border-radius: 4px; display: none; margin: 0 auto 4px; }
.pan2-upload-preview.pan2-show { display: block; }

.pan2-ao-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px; }
@media(max-width:640px) { .pan2-ao-grid { grid-template-columns: 1fr 1fr; } }

.pan2-nav-btns { display: flex; justify-content: space-between; margin-top: 24px; padding-top: 16px; border-top: 1px solid var(--border); }
.pan2-btn {
  padding: 10px 24px; border-radius: var(--radius); font-size: 13px;
  font-weight: 700; cursor: pointer; border: none; transition: all .15s;
}
.pan2-btn-prev { background: #fff; border: 1.5px solid var(--border); color: var(--muted); }
.pan2-btn-prev:hover { border-color: var(--navy); color: var(--navy); }
.pan2-btn-next { background: var(--navy); color: #fff; }
.pan2-btn-next:hover { background: #283593; }
.pan2-btn-submit { background: var(--gold); color: var(--navy); font-size: 14px; padding: 12px 32px; }
.pan2-btn-submit:hover { background: #b8962e; }

.pan2-summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.pan2-summary-item { padding: 12px; background: var(--light); border-radius: var(--radius); }
.pan2-summary-item .pan2-s-label { font-size: 11px; color: var(--muted); margin-bottom: 3px; }
.pan2-summary-item .pan2-s-value { font-size: 13px; font-weight: 600; color: var(--text); }
@media(max-width:640px) { .pan2-summary-grid { grid-template-columns: 1fr; } }

.pan2-progress-bar { height: 3px; background: var(--border); margin-bottom: 20px; border-radius: 2px; overflow: hidden; }
.pan2-progress-fill { height: 100%; background: var(--gold); border-radius: 2px; transition: width .3s; }

.pan2-submit-result {
  margin-top: 16px; padding: 14px; background: #fff8e8; border: 1.5px solid var(--gold);
  border-radius: var(--radius); font-size: 12px; color: var(--text);
}
.pan2-submit-result pre {
  margin-top: 8px; max-height: 240px; overflow: auto; background: #fff;
  border: 1px solid var(--border); border-radius: 4px; padding: 10px; font-size: 11px;
}

.pan2-pin-section { max-width: 260px; }

.pan2-submit-error {
  margin-top: 16px; padding: 12px 14px; background: #fdeceb;
  border: 1.5px solid var(--error); border-radius: var(--radius);
  font-size: 13px; color: var(--error); font-weight: 600;
}

.pan2-submit-success {
  background: #e8f5ee; border-color: var(--success);
}
.pan2-submit-success strong { color: var(--success); font-size: 14px; display: block; margin-bottom: 6px; }
.pan2-ref-no { font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 10px; }
.pan2-pdf-links { display: flex; gap: 10px; flex-wrap: wrap; }
.pan2-pdf-links a {
  background: var(--navy); color: #fff; text-decoration: none;
  padding: 8px 16px; border-radius: var(--radius); font-size: 12px; font-weight: 700;
  transition: background .15s;
}
.pan2-pdf-links a:hover { background: #283593; }

.pan2-btn:disabled { opacity: .6; cursor: not-allowed; }
`;